<template>
    <div :class="['notification-wrapper', direction]">
        <div class="notification-container">
            <notification-content
                v-for="notification in notifications"
                :key="notification.id"
                v-bind="notification"
                :direction="direction"
                @close="removeNotification(notification.id)"
            ></notification-content>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref } from 'vue';
    import NotificationContent from '@/components/service-components/NotificationContentService.vue';
    import {
        NotificationDirection,
        NotificationType,
        type NotificationItem,
        type NotificationOptions,
    } from '@/types/components/base-components.types';

    // ----- state -----
    const notifications = ref<NotificationItem[]>([]);
    const direction = ref<NotificationDirection>(
        NotificationDirection.TopRight
    );

    // ----- methods -----
    // timeout 轉為正整數，無效值一律視為 0
    const parseTimeout = (
        timeout: number | string | null | undefined
    ): number => {
        const parsed = parseInt(String(timeout), 10);
        return !Number.isNaN(parsed) && parsed > 0 ? parsed : 0;
    };

    // 初始化通知資料
    const init = (
        options: NotificationOptions = {},
        type: NotificationType
    ): NotificationItem => {
        const requestedDirection = Object.prototype.hasOwnProperty.call(
            options,
            'direction'
        )
            ? options.direction || NotificationDirection.TopRight
            : NotificationDirection.TopRight;
        direction.value = requestedDirection;

        // center 位置固定單筆顯示，避免堆疊造成視覺混淆
        const requestedStack = Object.prototype.hasOwnProperty.call(
            options,
            'stack'
        )
            ? Boolean(options.stack)
            : true;
        const stack =
            requestedDirection === NotificationDirection.Center
                ? false
                : requestedStack;
        if (!stack) {
            notifications.value = [];
        }

        // persistent 時不做 timeout 自動關閉
        const persistent = Object.prototype.hasOwnProperty.call(
            options,
            'persistent'
        )
            ? Boolean(options.persistent)
            : false;

        const timeout = Object.prototype.hasOwnProperty.call(options, 'timeout')
            ? parseTimeout(options.timeout)
            : 3000;

        // closable: 是否顯示關閉按鈕，常駐通知預設顯示
        const closable = Object.prototype.hasOwnProperty.call(
            options,
            'closable'
        )
            ? Boolean(options.closable)
            : persistent;

        return {
            id: crypto.randomUUID(),
            type,
            title: Object.prototype.hasOwnProperty.call(options, 'title')
                ? options.title || ''
                : '',
            body: Object.prototype.hasOwnProperty.call(options, 'body')
                ? options.body || ''
                : '',
            icon: Object.prototype.hasOwnProperty.call(options, 'icon')
                ? options.icon || ''
                : '',
            timeout: persistent ? 0 : timeout,
            closable,
            persistent,
            stack,
        };
    };

    // 顯示一般通知
    const notify = (options: NotificationOptions = {}): void => {
        const nextNotification = init(options, NotificationType.Basic);
        notifications.value.push(nextNotification);
    };

    // 顯示警告通知
    const warn = (options: NotificationOptions = {}): void => {
        const nextNotification = init(options, NotificationType.Warning);
        notifications.value.push(nextNotification);
    };

    // 顯示錯誤通知
    const error = (options: NotificationOptions = {}): void => {
        const nextNotification = init(options, NotificationType.Danger);
        notifications.value.push(nextNotification);
    };

    // 移除指定通知
    const removeNotification = (id: string): void => {
        notifications.value = notifications.value.filter(
            (item) => item.id !== id
        );
    };

    // 關閉所有通知
    const clearAll = (): void => {
        notifications.value = [];
    };

    defineExpose({
        notify,
        warn,
        error,
        clearAll,
    });
</script>

<style lang="scss" scoped>
    .notification-wrapper {
        position: fixed;
        max-width: 343px;
        z-index: 9000;

        &.top-left {
            top: 1%;
            left: 1rem;

            @media (max-width: 576px) {
                left: 16px;
            }
        }

        &.top {
            top: 1%;
            left: 50%;
            transform: translateX(-50%);
        }

        &.top-right {
            top: 1%;
            right: calc(1rem + 50px + 16px);

            @media (max-width: calc(343px + 1rem + 50px + 16px * 2)) {
                right: 16px;
            }
        }

        &.left {
            top: 50%;
            left: 1rem;
            transform: translateY(-50%);

            @media (max-width: 576px) {
                left: 16px;
            }
        }

        &.center {
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }

        &.right {
            top: 50%;
            right: calc(1rem + 50px + 16px);
            transform: translateY(-50%);

            @media (max-width: calc(343px + 1rem + 50px + 16px * 2)) {
                right: 16px;
            }
        }

        &.bottom-left {
            bottom: 75px;
            left: 1rem;

            @media (max-width: 576px) {
                left: 16px;
            }
        }

        &.bottom {
            bottom: 75px;
            left: 50%;
            transform: translateX(-50%);
        }

        &.bottom-right {
            bottom: 75px;
            right: calc(1rem + 50px + 16px);

            @media (max-width: calc(343px + 1rem + 50px + 16px * 2)) {
                right: 16px;
            }
        }

        @media (max-width: calc(343px + 16px * 2)) {
            max-width: calc(100% - 16px * 2);
        }

        .notification-container {
            display: flex;
            flex-direction: column;
            gap: 8px;
            justify-content: center;
            max-width: 100%;
            pointer-events: none;
        }

        &.top-left,
        &.left,
        &.bottom-left {
            .notification-container {
                align-items: flex-start;
            }
        }

        &.top,
        &.center,
        &.bottom {
            .notification-container {
                align-items: center;
            }
        }

        &.top-right,
        &.right,
        &.bottom-right {
            .notification-container {
                align-items: flex-end;
            }
        }
    }
</style>
